import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionTag, SectionTitle, StatusBadge } from "./shared";
import { IMG } from "../data/site";
import { Clapperboard, Users, Footprints, Hotel, UtensilsCrossed, ShoppingBag, Wrench, Home } from "lucide-react";

const FLOW = [
  { icon: Clapperboard, label: "Film Production" },
  { icon: Users, label: "Employment" },
  { icon: Footprints, label: "Visitors" },
  { icon: Hotel, label: "Hotels" },
  { icon: UtensilsCrossed, label: "Restaurants" },
  { icon: ShoppingBag, label: "Retail" },
  { icon: Wrench, label: "Services" },
  { icon: Home, label: "Housing" },
];

export function FilmCity() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.18, 1]);

  return (
    <section ref={ref} className="relative py-28 lg:py-44 overflow-hidden bg-[#0A0A0A]" data-testid="film-city-section">
      <motion.div className="absolute inset-0" style={{ scale }}>
        <img src={IMG.filmCity} alt="International Film City studio" className="w-full h-full object-cover img-cinematic opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-black/50 to-[#0A0A0A]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTag>Film City</SectionTag>
        <div className="max-w-3xl mb-6">
          <SectionTitle className="text-4xl sm:text-5xl lg:text-7xl">
            Where Entertainment Meets <span className="text-gold-gradient italic">Urban Growth.</span>
          </SectionTitle>
        </div>
        <Reveal delay={0.15}>
          <div className="flex items-center gap-4 mb-8">
            <StatusBadge status="Announced" />
            <span className="text-[11px] uppercase tracking-[0.25em] text-[#71717A]">Proposed International Film City &mdash; YEIDA</span>
          </div>
          <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed max-w-xl">
            Sound stages, production facilities, hotels, restaurants and a night-time entertainment
            district. A creative economy has the potential to ripple outward - through employment,
            visitors and services - into sustained housing demand.
          </p>
        </Reveal>

        <div className="mt-16 flex flex-wrap items-center gap-y-6" data-testid="film-city-flow">
          {FLOW.map((f, i) => (
            <Reveal key={f.label} delay={0.1 + i * 0.08} className="flex items-center">
              <div className="flex items-center gap-3 pr-6">
                <f.icon size={18} strokeWidth={1.4} className="text-[#D4AF37]" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#A1A1AA]">{f.label}</span>
              </div>
              {i < FLOW.length - 1 && <span className="hidden sm:block h-px w-8 bg-gradient-to-r from-[#D4AF37]/60 to-[#D4AF37]/10 mr-6" />}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
